import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Car, Wrench, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

/**
 * Role selection step — shown after a successful registration when the
 * backend hasn't yet stamped the user with a role. Lets the user pick
 * between Car Owner or Repair Shop (and provide shop-specific details
 * inline) before being routed into the correct dashboard.
 *
 * Bug fixes applied here:
 *  - Continue button now has an explicit `type="button"` so it never
 *    accidentally inherits submit semantics from an enclosing form (which
 *    was making taps appear unresponsive on mobile WebKit).
 *  - The Continue handler always resets `loading` even if `setRole`
 *    throws synchronously — previously a swallowed error would leave the
 *    button disabled forever.
 *  - Added a visible Back arrow in the top-left so users can return to
 *    the previous screen / change their mind without force-quitting.
 */
export default function Onboarding() {
  const { user, setUser, logout } = useAuth();
  const navigate = useNavigate();
  const [pick, setPick] = useState("owner");
  const [shopName, setShopName] = useState("");
  const [bio, setBio] = useState("");
  const [loading, setLoading] = useState(false);
  // 18+ confirmation for first-time Google / Apple signups (p1-alignment-nov2).
  // Email signups already ticked it on the register form (age_confirmed_at set).
  const [ageConfirmed, setAgeConfirmed] = useState(false);

  // Guard: when the user is logged out, send them to /login. This MUST live
  // in a useEffect (not in render) — otherwise React schedules the
  // navigate('/login') during render and it races with the back() handler's
  // navigate('/'), causing the Back button to land on /login instead of /.
  // Reported by testing agent in iter16.
  useEffect(() => {
    if (!user) navigate("/login", { replace: true });
  }, [user, navigate]);

  if (!user) {
    return null;
  }

  const submit = async () => {
    if (loading) return; // re-entrancy guard against double-taps
    if (pick === "shop" && !shopName.trim()) {
      toast.error("Shop name is required");
      return;
    }
    const needsAge = !user.age_confirmed_at;
    if (needsAge && !ageConfirmed) {
      toast.error("Please confirm you are 18 or older to continue.");
      return;
    }
    setLoading(true);
    try {
      // Previously called a `setRole` that AuthContext never exported, so
      // first-time Google/Apple users could not finish onboarding.
      const res = await api.post("/auth/role", {
        role: pick,
        shop_name: pick === "shop" ? shopName : null,
        bio: pick === "shop" ? bio : null,
        ...(needsAge ? { age_confirmed: ageConfirmed } : {}),
      });
      const u = res.data;
      setUser(u);
      navigate(u.role === "shop" ? "/shop" : "/owner", { replace: true });
    } catch (e) {
      // Surface ALL failure modes (network, server 4xx, hung promise) so the
      // user gets actionable feedback and the button becomes tappable again.
      console.error("Onboarding setRole failed:", e);
      toast.error(e?.response?.data?.detail || e?.message || "Could not save role — please retry");
    } finally {
      setLoading(false);
    }
  };

  // "Back" on the role-selection screen logs the partial-registration user
  // out and returns them to the landing page. We navigate BEFORE awaiting
  // logout so the URL change happens immediately — otherwise the useEffect
  // guard above would see user==null and overwrite the URL with /login.
  const back = async () => {
    navigate("/");
    try { await logout(); } catch { /* logout is best-effort */ }
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col">
      {/* Top bar with back arrow */}
      <div className="max-w-[1400px] w-full mx-auto px-4 sm:px-8 lg:px-12 py-8 flex items-center">
        <button
          type="button"
          onClick={back}
          className="flex items-center gap-2 text-zinc-300 hover:text-white text-sm font-display tracking-wide transition-colors"
          data-testid="onb-back-btn"
          aria-label="Back to home"
        >
          <ArrowLeft className="w-4 h-4" />
          <span className="hidden sm:inline">Back</span>
        </button>
      </div>

      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-2xl border border-white/10 bg-[#121214] p-8 sm:p-12">
          <div className="label-eyebrow mb-4">ONE MORE STEP</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter mb-3">
            Are you fixing or getting fixed?
          </h1>
          <p className="text-zinc-400 mb-10">Choose your role. This unlocks the right dashboard.</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
            <button
              type="button"
              onClick={() => setPick("owner")}
              className={`p-6 border text-left transition-all ${pick === "owner" ? "border-[#FF3B30] bg-[#FF3B30]/10" : "border-white/10 hover:border-white/30"}`}
              data-testid="pick-owner"
            >
              <Car className="w-7 h-7 mb-4 text-[#FF3B30]" strokeWidth={1.5} />
              <div className="font-heading text-lg font-bold mb-1">CAR OWNER</div>
              <div className="text-sm text-zinc-500">Post issues, collect anonymous quotes, choose your mechanic.</div>
            </button>
            <button
              type="button"
              onClick={() => setPick("shop")}
              className={`p-6 border text-left transition-all ${pick === "shop" ? "border-[#FF3B30] bg-[#FF3B30]/10" : "border-white/10 hover:border-white/30"}`}
              data-testid="pick-shop"
            >
              <Wrench className="w-7 h-7 mb-4 text-[#FF3B30]" strokeWidth={1.5} />
              <div className="font-heading text-lg font-bold mb-1">REPAIR SHOP</div>
              <div className="text-sm text-zinc-500">Browse open jobs, send private quotes, earn 5-star ratings.</div>
            </button>
          </div>

          {pick === "shop" && (
            <div className="space-y-4 mb-8">
              <div>
                <label className="label-eyebrow block mb-2">SHOP NAME</label>
                <input value={shopName} onChange={(e) => setShopName(e.target.value)} className="input-base" data-testid="onb-shop-name" placeholder="Main St Auto" />
              </div>
              <div>
                <label className="label-eyebrow block mb-2">SHORT BIO</label>
                <textarea value={bio} onChange={(e) => setBio(e.target.value)} rows={3} className="input-base" data-testid="onb-bio" placeholder="What you specialize in..." />
              </div>
            </div>
          )}

          {!user.age_confirmed_at && (
            <label className="flex items-start gap-3 text-sm text-zinc-300 cursor-pointer select-none mb-6" data-testid="onb-age-label">
              <input
                type="checkbox"
                checked={ageConfirmed}
                onChange={(e) => setAgeConfirmed(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[#FF9500]"
                data-testid="onb-age-checkbox"
              />
              <span>I am 18 or older.</span>
            </label>
          )}

          <button
            type="button"
            onClick={submit}
            disabled={loading}
            className="btn-primary w-full disabled:opacity-60"
            data-testid="onb-continue"
          >
            {loading ? "Saving…" : "Continue"}
          </button>

          {/* Subtle escape hatch — "Sign in with a different account" — so a
              user who landed here by mistake (e.g. from an old half-registered
              session) can still get to the login form without going via the
              landing page. */}
          <div className="mt-6 text-center text-xs text-zinc-500">
            Wrong account?{" "}
            <button
              type="button"
              onClick={async () => { navigate("/login"); try { await logout(); } catch {} }}
              className="text-white underline underline-offset-4"
              data-testid="onb-switch-account"
            >
              Sign in with another
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
